import React, { useState, useMemo } from "react";

import { useTypedSelector } from "../../../../redux/reducers/RootReducer";
import { getStorageConfigById } from "../../../../redux/reducers/StorageConfigReducer";
import {
  getSuiteMode,
  getSuiteTestingState,
} from "../../../../redux/reducers/ModuleStateReducer";

import { EFootprint } from "../../../../Model/PlateType";
import { ETestingType } from "../../../../Model/TestingParameter";
import { ESuiteMode, ETestingState } from "../../../../Model/ModuleState";

import CheckBox from "../../../Buttons/CheckBox";
import MultipleSelectDropDown, {
  GroupedDropDownDisplayValue,
} from "../../../Common/DropDown/MultipleSelectDropDown";
import SpinnerTextBox from "../../../Controls/SpinnerTextBox";
import ToolsContentButton from "../../ToolsCommon/ToolsContentButton";

import TestingApi from "../../../../api/TestingApi";

import styles from "./ScanningTest.module.css";

import { StorageConfig } from "../../../../Model/StorageConfig";
import { getModulesByType } from "../../../../redux/reducers/ModuleReducer";
import { EModuleType } from "../../../../Model/EModuleType";

export default function ScanningTest() {
  const suiteMode = useTypedSelector((state) => getSuiteMode(state));
  const testingState = useTypedSelector((state) => getSuiteTestingState(state));
  const chamberModules = useTypedSelector((state) => getModulesByType(state)[EModuleType.CHAMBER]);
  const loadPortModules = useTypedSelector((state) => getModulesByType(state)[EModuleType.LOADPORT]);
  const storageConfigByIdAll = useTypedSelector((state) =>
      getStorageConfigById(state)
    );
  
  const storageConfigById: { [index: string]: StorageConfig } = useMemo(() => {
    let temp: { [index: string]: StorageConfig } = {};
    chamberModules.forEach((module) => {
      if (storageConfigByIdAll[module.identifier]) {
        temp[module.identifier] = storageConfigByIdAll[module.identifier];
      }
    });
    loadPortModules.forEach((module) => {
      if (storageConfigByIdAll[module.identifier]) {
        temp[module.identifier] = storageConfigByIdAll[module.identifier];
      }
    });
    return temp;

  }, [storageConfigByIdAll, chamberModules, loadPortModules]);


  const [selectedHotels, setSelectedHotels] = useState([] as string[]);
  const [iteration, setIteration] = useState(0);
  const [showScanResult, setShowScanResult] = useState(false);

  const isDisabled = suiteMode !== ESuiteMode.Normal;
  const startEnabled =
    suiteMode !== ESuiteMode.Testing ||
    (suiteMode === ESuiteMode.Testing &&
      testingState.barcodeScan?.state === ETestingState.Stopped);
  const stopEnabled =
    suiteMode === ESuiteMode.Testing &&
    testingState.barcodeScan?.state === ETestingState.Started;

  const groupedDisplayValues: GroupedDropDownDisplayValue<string>[] =
    Object.keys(storageConfigById).map((identifier) => {
      return {
        title: identifier,
        displayValues: storageConfigById[identifier].hotels.map((hotel) => {
          return {
            display: `${EFootprint[hotel.footprint]} ${hotel.number}`,
            value: `${identifier}-@#$-hotel-@#$-${hotel.number}`,
            disabled: false,
          };
        }),
      };
    });

  const runTest = () => {
    const targetModuleHotelsMap = new Map<string, number[]>();

    selectedHotels.forEach((hotelString) => {
      const hotelStringSplited = hotelString.split("-@#$-");
      const moduleIdentifier = hotelStringSplited[0];
      const hotelNumber = parseInt(
        hotelStringSplited[hotelStringSplited.length - 1]
      );

      if (targetModuleHotelsMap.has(moduleIdentifier)) {
        const hotelNumbers = targetModuleHotelsMap.get(moduleIdentifier);
        if (hotelNumbers) {
          hotelNumbers.push(hotelNumber);
          targetModuleHotelsMap.set(moduleIdentifier, hotelNumbers);
        }
      } else {
        targetModuleHotelsMap.set(moduleIdentifier, [hotelNumber]);
      }
    });

    TestingApi.doTesting({
      moduleIdentifier: "Suite",
      type: ETestingType.BarcodeScan,
      barcodeScanTest: {
        displayScannedBarcodeResult: showScanResult,
        numIterations: iteration,
        moduleIdentifierHotelNumbersMap: Object.fromEntries(
          targetModuleHotelsMap
        ),
      },
    });
  };

  const stopTest = () => TestingApi.stopTesting();

  return (
    <div className="testing-content">
      <section className={styles.scanningTestForm}>
        <div className={styles.scanningTestFormCard}>
          <div className="testing-input-line item-3">
            <label>Test frequency (iterations)</label>:{" "}
            <SpinnerTextBox
              value={iteration}
              onValueChanged={setIteration}
              min={0}
              max={100000}
              style={{ height: 24, width: 205 }}
              disabled={isDisabled}
            />
          </div>
          <CheckBox
            id="scanning-test-display-checkbox"
            name="scanning-test-display-checkbox"
            label="Display Scanned Result"
            checked={showScanResult}
            onChange={() => setShowScanResult(!showScanResult)}
            colorVariant="alternate"
            style={{ fontSize: 14 }}
            disabled={isDisabled}
          />
        </div>
        <div className="testing-card">
          <div className="testing-input-line item-3">
            <label>Hotel</label>
            <div>:</div>
            <MultipleSelectDropDown
              dropDownDisplayValues={groupedDisplayValues}
              id="reliability-test-hotel-drop-down"
              values={selectedHotels}
              onChange={setSelectedHotels}
              style={{ height: 24, width: 205, fontSize: 14 }}
              colorVariant="alternate"
              disabled={isDisabled}
            />
          </div>
        </div>
        <div className="testing-button-group">
          <ToolsContentButton
            text="Stop"
            onClick={stopTest}
            disabled={!stopEnabled}
            variant="primary"
            colorVariant="black"
          />
          <ToolsContentButton
            text="run"
            onClick={runTest}
            disabled={selectedHotels.length < 1 || !startEnabled}
            variant="primary"
            colorVariant="default"
          />
        </div>
      </section>

      <div
        className={"testing-status-box"}
        style={{
          opacity: isDisabled ? 0.5 : 1,
          overflow: "auto",
        }}
      >
        <p>{"Counter: " + testingState.barcodeScan?.counter}</p>
        <p>Status: </p>
        <pre>{testingState.barcodeScan?.status}</pre>
      </div>
    </div>
  );
}
